import styled from 'styled-components'
import { ICustomerPVAtoms } from '../../store/CustomerPVAtom'
import { ICustomerLoadAtoms } from '../../store/CustomerLoadAtom'
import { ISellPlansAtoms } from '../../store/SellPlansAtom'
import { IPurchasePlansAtoms } from '../../store/PurchasePlansAtom'
import { IEconomicAnalysis, rows } from '../../store/EconomicAnalysis'
import { TableWrapper, UnifiedTable } from './index'
import { useCustomerData } from '../../hooks/useCustomerData'
import { useEffect, useState } from 'react'

//#region styled
const EconomicAnalysisWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 1em;
  font-size: 0.9rem;

  /* [추가] 오른쪽 영역을 세로로 정렬하기 위한 스타일 */
  .right-section {
    display: flex;
    flex-direction: column;
    gap: 1em; /* 라벨과 박스 사이 간격 */
  }
  .wrapper {
    display: flex;
    flex-direction: row;
    width: 100%;
    gap: 2.5em;
  }

  .table-section {
    display: flex;
    flex-direction: column;
    gap: 1em;
    width: 100%;
    min-height: 0;
    padding-bottom: 0;
  }

  .table-wrapper {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    position: relative;
    width: 100%;
    gap: 24px;

    .pv-customer,
    .customer {
      width: 100%;
      min-width: 0;
    }

    @media (max-width: 760px) {
      grid-template-columns: 1fr;

      .seperate-line {
        display: none;
      }
    }

    table {
      /* (기존 스타일 유지) */
      text-align: center;
      font-size: 0.75em;
      border-collapse: separate;
      border-spacing: 0;
      border: 1px solid #e6e6e6;
      border-radius: 8px;
      width: 100%;

      th {
        background-color: #f4f5f8;
        color: #333333;
      }

      td:first-child {
        padding: 15px;
      }

      th,
      td {
        padding: 12.8px 0;
        border-right: 1px solid #e6e6e6;
        border-bottom: 1px solid #e6e6e6;
      }

      th:last-child,
      td:last-child {
        border-right: none;
      }

      tr:last-child th,
      tr:last-child td {
        border-bottom: none;
      }

      th:first-child {
        border-top-left-radius: 8px;
      }

      th:last-child {
        border-top-right-radius: 8px;
        border-right: none;
      }

      tr:last-child td:first-child {
        border-bottom-left-radius: 8px;
      }

      tr:last-child td:last-child {
        border-bottom-right-radius: 8px;
        border-bottom: none;
      }
    }

    .subtitle-section {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      padding: 0 10px;
      margin-bottom: 13.5px;
    }

    .subtitle {
      color: #57c9a7;
      font-weight: 600;
      margin: 0;
    }

    .design-plan-link {
      color: #2f3c5e;
      font-family: SUIT;
      font-size: 14px;
      font-weight: 500;
      line-height: 17px;
      text-decoration: none;
      white-space: nowrap;
      cursor: pointer;

      &:hover {
        color: #57c9a7;
      }
    }

    .seperate-line {
      position: absolute;
      top: 34px;
      bottom: 32px;
      left: 50%;
      border-left: 1px dashed #c6c6c6;
      transform: translateX(-50%);
    }

    select {
      border: none;
      width: 100%;
      color: #b3b3b3;
    }

    .add-row-section {
      display: flex;
      justify-content: center;
      margin-top: 12px;
    }
  }

  .bottom-panel {
    /* (기존 스타일 유지) */
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 4px;
    width: 100%;
    min-height: 57px;
    background-color: #f4fcfa;
    border: 1px solid #c2ecdf;
    border-radius: 4px;
    cursor: pointer;
    font-size: 1em;
    color: #4d4d4d;
  }

  .result-section {
    display: flex;
    justify-content: center;
    align-items: center;
    justify-self: center;
    width: calc(39vw - 1.25em);
    height: 274px;
    border: 1px solid #e6e6e6;
    border-radius: 4px;

    .result {
      display: flex;
      gap: 1.5em;
      width: calc(80% + 1.25em);
      height: 144px;

      .generate-amount {
        display: flex;
        flex-direction: column;
        justify-content: center;
        gap: 1em;
        font-size: 1.125rem;
        font-weight: 400;
        width: 50%;
        color: #2f3c5e;

        span {
          color: #4d4d4d;
        }

        span:first-child {
          margin-right: 0.5em;
        }
      }

      .aggregation-profit {
        display: flex;
        flex-direction: column;
        gap: 0.8em; /* 간격 살짝 조정 */
        width: 50%;

        div {
          display: flex;
          justify-content: space-between;
        }

        .total-profit {
          span:nth-child(n + 2) {
            color: #57c9a7;
            font-weight: 700; /* 강조 */
          }
        }
      }

      .seperate-line {
        border-left: 1px solid #c6c6c6;
      }

      .line {
        width: 100%;
        background-color: #4d4d4d;
        border-bottom: 1px solid black;
      }
    }
  }
`

// [수정] opex props 추가
interface Props {
  loadIdList: IEconomicAnalysis[]
  setLoadIdList: React.Dispatch<React.SetStateAction<IEconomicAnalysis[]>>
  pvIdList: IEconomicAnalysis[]
  setPvIdList: React.Dispatch<React.SetStateAction<IEconomicAnalysis[]>>
}
//#endregion

export const EconomicAnalysis = (props: Props) => {
  const { loadIdList, setLoadIdList, pvIdList, setPvIdList } = props

  const [loadRow, setLoadRow] = useState<
    | rows<ISellPlansAtoms, ICustomerLoadAtoms>[]
    | rows<ISellPlansAtoms, ICustomerLoadAtoms>
  >([
    {
      index: '0',
      id: new Date().getTime() + 1,
      customer: [],
      plan: [],
    },
  ])
  const [pvRow, setPvRow] = useState<
    | rows<IPurchasePlansAtoms, ICustomerPVAtoms>[]
    | rows<IPurchasePlansAtoms, ICustomerPVAtoms>
  >([
    {
      index: '0',
      id: new Date().getTime(),
      customer: [],
      plan: [],
    },
  ])

  const { pvCustomerList, loadCustomerList, buyTOUList, sellTOUList } =
    useCustomerData()

  useEffect(() => {
    setPvIdList([
      {
        index: '0',
        customer: { _id: undefined },
        plan: { _id: undefined },
      },
    ])
    setLoadIdList([
      {
        index: '0',
        customer: { _id: undefined },
        plan: { _id: undefined },
      },
    ])
  }, [setLoadIdList, setPvIdList])

  const addPvRow = () => {
    const newRow = {
      index: String(
        (
          pvRow as {
            index: string
            id: number
            plan: Partial<IPurchasePlansAtoms>[]
            customer: Partial<ICustomerPVAtoms>[]
          }[]
        ).length
      ),
      id: new Date().getTime(),
      name: pvCustomerList.map((m) => m.name),
      customer: pvCustomerList,
      plan: buyTOUList,
    }

    setPvRow([
      ...(pvRow as {
        index: string
        id: number
        plan: Partial<IPurchasePlansAtoms>[]
        customer: Partial<ICustomerPVAtoms>[]
      }[]),
      newRow,
    ])

    setPvIdList([
      ...pvIdList,
      {
        index: String(pvIdList.length),
        customer: { _id: undefined },
        plan: { _id: undefined },
      },
    ])
  }

  const addLoadRow = () => {
    const newRow = {
      index: String(
        (
          loadRow as {
            index: string
            id: number
            plan: Partial<ISellPlansAtoms>[]
            customer: Partial<ICustomerLoadAtoms>[]
          }[]
        ).length
      ),
      id: new Date().getTime(),
      name: loadCustomerList.map((m) => m.name),
      customer: loadCustomerList,
      plan: sellTOUList,
    }

    setLoadRow([
      ...(loadRow as {
        index: string
        id: number
        plan: Partial<ISellPlansAtoms>[]
        customer: Partial<ICustomerLoadAtoms>[]
      }[]),
      newRow,
    ])

    setLoadIdList([
      ...loadIdList,
      {
        index: String(loadIdList.length),
        customer: { _id: undefined },
        plan: { _id: undefined },
      },
    ])
  }

  const deletePvRow = (id: string): void => {
    setPvRow(
      (
        pvRow as Array<{
          index: string
          id: number
          customer: Partial<ICustomerPVAtoms>[]
          plan: Partial<IPurchasePlansAtoms>[]
        }>
      ).filter((item) => item.index !== id)
    )

    setPvIdList(pvIdList.filter((item) => item.index !== id))
  }

  const deleteLoadRow = (id: string): void => {
    setLoadRow(
      (
        loadRow as Array<{
          index: string
          id: number
          customer: Partial<ICustomerLoadAtoms>[]
          plan: Partial<ISellPlansAtoms>[]
        }>
      ).filter((item) => item.index !== id)
    )

    setLoadIdList(loadIdList.filter((item) => item.index !== id))
  }

  return (
    <EconomicAnalysisWrapper>
      <div className="wrapper">
        <div className="table-section">
          <div className="table-wrapper">
            <TableWrapper
              onAdd={addPvRow}
              title="PV 발전사업자"
              designPlanPath="/home/electricity-purchase-plans"
            >
              <UnifiedTable<
                Partial<ICustomerPVAtoms>,
                Partial<IPurchasePlansAtoms>,
                IEconomicAnalysis
              >
                customers={pvCustomerList}
                deleteCustomerRow={deletePvRow}
                plans={buyTOUList}
                recoilValue={pvIdList}
                setRecoilValue={setPvIdList}
                rows={
                  pvRow as rows<
                    Partial<ICustomerPVAtoms>,
                    Partial<IPurchasePlansAtoms>
                  >
                }
              />
            </TableWrapper>
            <div className="seperate-line" />
            <TableWrapper
              onAdd={addLoadRow}
              title="수용가"
              designPlanPath="/home/electricity-sell-plans"
            >
              <UnifiedTable<
                Partial<ICustomerLoadAtoms>,
                Partial<ISellPlansAtoms>,
                IEconomicAnalysis
              >
                customers={loadCustomerList}
                deleteCustomerRow={deleteLoadRow}
                plans={sellTOUList}
                recoilValue={loadIdList}
                setRecoilValue={setLoadIdList}
                rows={loadRow}
              />
            </TableWrapper>
          </div>
        </div>
      </div>
    </EconomicAnalysisWrapper>
  )
}
